/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useState } from 'react';

import { LaunchesService } from '../services/launchesApi';
import type { LaunchMaterial, MaterialCategory } from '../types';

interface UseLaunchMaterialsReturn {
  materials: LaunchMaterial[];
  categories: MaterialCategory[];
  typologyKeys: string[];
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}

/**
 * Loads a launch's materials and the agency's category vocabulary.
 *
 * Categories come from the server rather than a constant: the six fixed
 * ones are joined by any the agency created, so the picker and the library
 * sections are whatever this agency actually uses.
 */
export function useLaunchMaterials(
  agencyId: string,
  launchId: string,
): UseLaunchMaterialsReturn {
  const [materials, setMaterials] = useState<LaunchMaterial[]>([]);
  const [categories, setCategories] = useState<MaterialCategory[]>([]);
  const [typologyKeys, setTypologyKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const load = useCallback(() => {
    if (!agencyId || !launchId) return;
    setIsError(false);
    Promise.all([
      LaunchesService.materials(agencyId, launchId),
      LaunchesService.materialCategories(agencyId),
    ])
      .then(([library, cats]) => {
        setMaterials(library.materials ?? []);
        setTypologyKeys(library.typology_keys ?? []);
        setCategories(cats ?? []);
      })
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [agencyId, launchId]);

  useEffect(() => {
    setIsLoading(true);
    load();
  }, [load]);

  return { materials, categories, typologyKeys, isLoading, isError, retry: load };
}
